import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ClipboardCheck,
  Calendar,
  DollarSign,
  BarChart3,
  FileBarChart,
  ArrowRight,
  Users,
  MapPin,
  BookOpen,
  Landmark,
  TrendingUp,
  Scale,
  Package,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react';
import { useCompany } from '@/hooks/use-company';
import { tallyStatus } from '@/services/integration';
import { TALLY_REPORTS } from '@/lib/tally-reports';

export const Route = createFileRoute('/(app)/company/reports/')({
  component: ReportsIndexPage,
});

interface ReportType {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: string;
  enabled: boolean;
}

const REPORT_TYPES: ReportType[] = [
  {
    id: 'approvals',
    title: 'Approval Reports',
    description: 'View and analyze approval workflows including leave requests, expenses, and more',
    path: '/company/reports/aprovals',
    icon: 'clipboard-check',
    enabled: true,
  },
  {
    id: 'attendance',
    title: 'Attendance Report',
    description: 'View detailed attendance records, late arrivals, and absence patterns for all employees',
    path: '/company/reports/attendance',
    icon: 'calendar',
    enabled: true,
  },
  {
    id: 'salary',
    title: 'Salary Report',
    description: 'Generate comprehensive salary reports including breakdowns, deductions, and payment summaries',
    path: '/company/reports/salary',
    icon: 'currency-dollar',
    enabled: false,
  },
  {
    id: 'leave',
    title: 'Leave Report',
    description: 'Track leave balances, requests, and approvals across your organization',
    path: '/company/reports/leave',
    icon: 'calendar-event',
    enabled: false,
  },
  {
    id: 'performance',
    title: 'Performance Report',
    description: 'Analyze employee performance metrics, reviews, and productivity trends',
    path: '/company/reports/performance',
    icon: 'chart-bar',
    enabled: false,
  },
  {
    id: 'employee',
    title: 'Employee Report',
    description: 'Access employee records, demographics, and organizational structure data',
    path: '/company/reports/employee',
    icon: 'users',
    enabled: false,
  },
  {
    id: 'tracking',
    title: 'Location Tracking Report',
    description: 'View employee location history, check-in/check-out logs, and field activity',
    path: '/company/reports/tracking',
    icon: 'map-pin',
    enabled: false,
  },
];

// Icon mapping for report types
const getReportIcon = (iconName: string) => {
  switch (iconName) {
    case 'clipboard-check':
      return ClipboardCheck;
    case 'calendar':
      return Calendar;
    case 'currency-dollar':
      return DollarSign;
    case 'chart-bar':
      return BarChart3;
    case 'users':
      return Users;
    case 'map-pin':
      return MapPin;
    default:
      return FileBarChart;
  }
};

interface ReportCardProps {
  report: ReportType;
}

function ReportCard({ report }: ReportCardProps) {
  const Icon = getReportIcon(report.icon);

  if (!report.enabled) {
    return (
      <Card className="relative overflow-hidden opacity-60 cursor-not-allowed">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                <Icon className="size-5 text-muted-foreground" />
              </div>
              <CardTitle className="text-lg">{report.title}</CardTitle>
            </div>
            <Badge variant="secondary" className="text-xs">
              Coming Soon
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-sm">
            {report.description}
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  return (
    <Link to={report.path}>
      <Card className="relative overflow-hidden transition-all hover:shadow-md hover:border-primary/50 cursor-pointer group">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                <Icon className="size-5 text-primary" />
              </div>
              <CardTitle className="text-lg">{report.title}</CardTitle>
            </div>
            <ArrowRight className="size-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-sm">
            {report.description}
          </CardDescription>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  THE ACCOUNTING REPORTS — a section of their own.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Kept apart from the HR reports above rather than mixed in, because they come
 * from somewhere else and can be unavailable for their own reasons: these read
 * a TallyPrime running on a computer in the customer's office, and it is
 * switched off at night. A "Balance Sheet" card sitting between "Attendance"
 * and "Salary" would imply the same availability as the rest of the page.
 *
 * The whole section is HIDDEN when the company has no Tally connected — not
 * greyed out like the "Coming Soon" cards above, because those are features we
 * have not built, while this is one the company simply does not use. Offering
 * a locked accounting section to a company that has no accounting integration
 * is an advert, not a report.
 */
const TALLY_ICONS: Record<string, typeof Landmark> = {
  'balance-sheet': Scale,
  'profit-loss': TrendingUp,
  'trial-balance': Landmark,
  'day-book': BookOpen,
  'stock-summary': Package,
  'bills-outstanding': ArrowDownLeft,
  'bills-outstanding-payable': ArrowUpRight,
};

function TallyReportsSection() {
  const { selectedCompanyId } = useCompany();

  const status = useQuery({
    queryKey: ['tally-status', selectedCompanyId],
    queryFn: () => tallyStatus(selectedCompanyId as number),
    enabled: Boolean(selectedCompanyId),
    // Whether a company has Tally, and which one — see the note in
    // `tally-report-view.tsx`. Restored from disk it decides, wrongly,
    // whether this whole section appears.
    meta: { persist: false },
  });

  if (!status.data?.provisioned) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Landmark className="size-5 text-primary" />
        <div>
          <h2 className="text-lg font-semibold">Accounts</h2>
          <p className="text-sm text-muted-foreground">
            Read straight from this company's Tally. Every figure is Tally's own — nothing here
            recalculates them.
          </p>
        </div>
      </div>

      {/*
        Said once, here, rather than on each of seven cards. Tally is a desktop
        application somebody closes at night, so "not right now" is a normal
        state and not a fault — but each report states its own age when opened,
        which is the number that actually matters.
      */}
      {!status.data.connected && (
        <p className="rounded-md border p-3 text-sm text-muted-foreground">
          The connector for this company is offline, so these will show the last figures we hold
          rather than today's. Each report says how old it is.
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {TALLY_REPORTS.map((report) => {
          const Icon = TALLY_ICONS[report.id] ?? FileBarChart;
          return (
            <Link key={report.id} to="/company/reports/tally/$report" params={{ report: report.id }}>
              <Card className="group relative cursor-pointer overflow-hidden transition-all hover:border-primary/50 hover:shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{report.title}</CardTitle>
                    </div>
                    <ArrowRight className="size-5 text-muted-foreground transition-all group-hover:translate-x-1 group-hover:text-primary" />
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm">{report.description}</CardDescription>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ReportsIndexPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-t from-primary/5 to-card dark:bg-card">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <FileBarChart className="size-6 text-primary" />
            <div>
              <CardTitle className="text-2xl">Reports</CardTitle>
              <CardDescription>
                Generate and view various reports for your organization
              </CardDescription>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Cards Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {REPORT_TYPES.map((report) => (
          <ReportCard key={report.id} report={report} />
        ))}
      </div>

      <TallyReportsSection />
    </div>
  );
}
